import { GROUP_TYPE } from "@/constants/area-constants";
import { FC } from "react";
import { useSelector } from "react-redux";
import TableTab from "../components/tabs/table-tab";
import { ACCESS_GROUPS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from "../models/ColumnDefinitions/AccessGroupColumns";
import { ListQuery } from "../models/ListQuery";
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import AccessAreaSearch from "../models/search/AccessAreaSearch";
import searchColumns from "../models/search/AccessGroupSearch";
import AccessTimePatternSearch from "../models/search/AccessTimePatternSearch";
import SiteSearch from "../models/search/SiteSearch";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { getAccessAreaNamesWithPagination } from "../services/accessAreaService";
import { deleteAccessGroup, getAllAccessGroups } from "../services/accessGroupService";
import { getAccessTimePatternNames } from '../services/accessTimePatternService';
import { getSiteNamesWithPagination } from "../services/siteService";
import { getDayNames } from "../utilities/DateUtils";
import { ACCESS_TYPES } from "@/constants/access-constants";

interface Props {
    setBusy: (isBusy: boolean) => void,
    categories: Category[],
    setCategories: (category: Category[]) => void,
    onFilterChange?: (filter: any) => void,
    accesType: GROUP_TYPE;
}

const AccessTab: FC<Props> = ({ setBusy, categories, setCategories, onFilterChange, accesType }) => {
    const { translate } = useLocale();
    const itemPath = accesType === ACCESS_TYPES.ACCESS_GROUP ? '/access-control/enrolment/access-group/' : '/access-control/enrolment/floor-group/';
    const title = accesType === ACCESS_TYPES.ACCESS_GROUP ? translate.byKey(TranslationKeys.accessGroups) : translate.byKey(TranslationKeys.floorGroups);
    const userLoginId = useSelector((state: any) => (state.userInfo.id))
    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.accessTimePattern,
            values: [],
            dataMethod: getAccessTimePatternNames,
            searchAccessor: AccessTimePatternSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.area,
            values: [],
            dataMethod: (listQuery?:ListQuery) => getAccessAreaNamesWithPagination(accesType, listQuery),
            searchAccessor: AccessAreaSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.days,
            values: getDayNames(translate)
        }
    ];

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteAccessGroup(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        const newCatergories: Category[] = categories.map((category) => {
                            return category.id === 0 ? {...category, count: (category?.count ?? 1) - 1,}: category;
                        });
                        setCategories(newCatergories);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const initialiseUserPreferences = (setDetails: (userLoginId, ACCESS_GROUPS) => void) => {
        setDetails(userLoginId, ACCESS_GROUPS);
    }

    return <TableContextProvider
        dataMethod={(listParameters?: ListQuery) => getAllAccessGroups(accesType, translate, listParameters)}
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useFilter
        useSearch
        useSort
        deleteMethod={onDelete}
        lookup={lookup}
        customDelete={{ fieldName: "isDefault", comparisonValue: false }}>
        <TableContextStateMonitor onFilterChange={onFilterChange}/>
        <TableTab
            title={title}
            selectRows={false}
            rowPath={itemPath}
            cyName={accesType === 1 ? "accessGroup" : "floorGroup"} />
    </TableContextProvider>;
};

export default AccessTab;
